import {User as FirebaseUser} from 'firebase/auth';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {RelationshipInfo} from '../../backend/interfaces/relationship-info';
import {User} from '../../backend/interfaces/user';
import {PushNotificationsState} from '../../enums/push-notifications-state';
import {SignInError} from '../../enums/sign-in-error';
import {InviteIdentification} from '../../interfaces/invite-identification';
import {Session} from '../../interfaces/session';
import {RolePermissionType} from '../../backend/enums/role-permission-type';

export interface AuthContextState {
  isRestoringSession: boolean;
  pushNotificationsState: PushNotificationsState;
  firebaseUser?: FirebaseUser;
  session?: Session;
}

export enum AuthContextActionType {
  RESTORE_SESSION = 'RESTORE_SESSION',
  UPDATE_SESSION = 'UPDATE_SESSION',
  SIGN_IN = 'SIGN_IN',
  SIGN_OUT = 'SIGN_OUT',
  SET_PUSH_NOTIFICATIONS_STATE = 'SET_PUSH_NOTIFICATIONS_STATE',
}

export type AuthContextAction =
  | {
      type: AuthContextActionType.RESTORE_SESSION;
      firebaseUser?: FirebaseUser;
      session?: Session;
    }
  | {
      type: AuthContextActionType.SIGN_IN;
      firebaseUser?: FirebaseUser;
      session?: Session;
    }
  | {
      type: AuthContextActionType.SIGN_OUT;
    }
  | {
      type: AuthContextActionType.UPDATE_SESSION;
      session: Session;
    }
  | {
      type: AuthContextActionType.SET_PUSH_NOTIFICATIONS_STATE;
      pushNotificationsState: PushNotificationsState;
    };

export interface SignInCredentials {
  email: string;
  password: string;
}
export type VoximplantIncomingPushDisposition =
  | 'ready'
  | 'queued-until-healthy'
  | 'duplicate-visible-ui';

export interface AuthContextType {
  state: AuthContextState;

  // Methods
  restorePreviousSession: () => Promise<void>;
  restorePreviousSessionByQuickAccess: (
    authToken: string,
    firebaseUser: FirebaseUser
  ) => Promise<void>;
  signIn: (
    credentials: SignInCredentials,
    retryOnWrongPassword: boolean,
    inviteAfterLogin?: InviteIdentification
  ) => Promise<SignInError | undefined>;
  signInAfterCreatingAccount: (data: SignInCredentials) => Promise<void>;
  signInLegacyIntegration: (authToken: string) => Promise<SignInError | undefined>;
  signOut: () => Promise<void>;
  signOutLegacyIntegration: () => Promise<void>;
  deferVoximplantIncomingPushUntilHealthy: (
    notification: any,
    action: string,
    reason: string
  ) => VoximplantIncomingPushDisposition;
  recoverVoximplantIncomingPush: (
    notification: any,
    reason: string,
    action?: string
  ) => Promise<void>;
  clearPendingForegroundVoximplantPushRecovery: (
    reason: string,
    options?: {clearPlaceholder?: boolean}
  ) => void;
  refreshSessionData: (
    selectedRelationshipId?: string,
    selectedAffiliationId?: string
  ) => Promise<void>;
  updateSelectedAffiliation: (affiliation: AffiliationInfo) => void;
  updateSelectedRelationship: (relationship: RelationshipInfo) => void;
  updateSessionCurrentUser: (user: User) => void;
  updateSessionUnreadNotificationCount: (count: number) => void;
  setPushNotificationsState: (pushNotificationsState: PushNotificationsState) => void;
  subscribeFirebasePushNotifications: (subscribeToIncomingCalls: boolean) => Promise<void>;
  unsubscribeFirebasePushNotifications: () => Promise<void>;
  removeRelationship: (relationshipId: string, expectedUserId?: string) => void;
  removeAffiliation: (entityId: string) => void;
  hasReadPermission: (rolePermissionType: RolePermissionType) => boolean;
  hasWritePermission: (rolePermissionType: RolePermissionType) => boolean;
  signInWithMicrosoft: (inviteAfterLogin?: InviteIdentification) => Promise<void>;
  signInWithGoogle: (inviteAfterLogin?: InviteIdentification) => Promise<void>;
}
